import { MUSIC_VOLUMES, NOISE_REDUCTION_MODES } from './audio.js';
import { TRANSITIONS, TRANSITION_MIN_S, TRANSITION_MAX_S } from './transitions.js';
import { z } from 'zod';
import { COLOR_FILTERS } from './color.js';
import {
  DEFAULT_SETTINGS as DEFAULTS,
  HIGHLIGHT_COLORS,
  HOOK_ACCENTS,
  HOOK_STYLES,
  SUBTITLE_BOXES,
  SUBTITLE_SIZES,
  MOODS,
  SUBTITLE_FONTS,
  LEGACY_SUBTITLE_FONTS,
  SUBTITLE_PRESETS,
} from './constants.js';

export {
  HIGHLIGHT_COLORS,
  HOOK_STYLES,
  HIGHLIGHT_COLOR_INFO,
  LIMITS,
  MIN_DURATION_S,
  MOODS,
  SUBTITLE_FONTS,
  SUBTITLE_FONT_INFO,
  SUBTITLE_PRESET_FONT,
  SUBTITLE_PRESETS,
  type HighlightColor,
  type Mood,
  type SubtitleFont,
  type SubtitleFontInfo,
  type SubtitlePreset,
} from './constants.js';

export const ProjectSettingsSchema = z.object({
  editingMode: z.enum(['full', 'summary']).default(DEFAULTS.editingMode),
  /** Максимальная длина выжимки. В полном рассказе не ограничивает длительность. */
  // Совместимость со старым полем duration=full; режим задаётся отдельно.
  duration: z.preprocess(value => value === 'full' ? 'auto' : value,
    z.enum(['auto', '30', '60', '90'])).default(DEFAULTS.duration),
  hook: z.boolean().default(DEFAULTS.hook),
  hookStyle: z.enum(HOOK_STYLES).default(DEFAULTS.hookStyle),
  /** Как выделять акцентную часть хука: «Нет», цветом или цветом и капсом. */
  hookAccent: z.enum(HOOK_ACCENTS).default(DEFAULTS.hookAccent),
  /** Ускорение готового ролика: речь, видео и субтитры, без музыки и хука. */
  playbackSpeed: z.union([z.literal(1), z.literal(1.2), z.literal(1.5), z.literal(1.7), z.literal(2)]).default(DEFAULTS.playbackSpeed),
  /** Вырезать паузы и слова-паразиты. */
  cleanPauses: z.boolean().default(DEFAULTS.cleanPauses),
  subtitles: z.enum(['off', ...SUBTITLE_PRESETS]).default(DEFAULTS.subtitles),
  /** 'top' / 'bottom' или свободное положение от 0 (верх безопасной зоны) до 1 (низ). */
  subtitlePosition: z.union([z.enum(['top', 'bottom']), z.number().min(0).max(1)]).default(DEFAULTS.subtitlePosition),
  /** Подложка под строками субтитров. */
  subtitleBox: z.enum(SUBTITLE_BOXES).default(DEFAULTS.subtitleBox),
  /** Размер субтитров: мелкий, средний или крупный. */
  subtitleSize: z.enum(SUBTITLE_SIZES).default(DEFAULTS.subtitleSize),
  /** Ручное положение хука 0…1; действует только при ручном положении субтитров, null — напротив них. */
  hookPosition: z.number().min(0).max(1).nullable().default(DEFAULTS.hookPosition),
  /** Тихий звук клавиши при появлении слова в готовом видео. */
  subtitleSound: z.boolean().default(DEFAULTS.subtitleSound),
  /** Лёгкое перекрытие крупного слова головой, только в стиле «Акцент». */
  subtitleDepth: z.boolean().default(DEFAULTS.subtitleDepth),
  /** Короткие приближения на смысловых акцентах, с возвратом к общему плану. */
  accentZooms: z.boolean().default(DEFAULTS.accentZooms),
  /** Цвет акцента в Editorial и текущего слова в Karaoke и Bold. */
  highlight: z.enum(HIGHLIGHT_COLORS).default(DEFAULTS.highlight),
  /** Шрифт субтитров, хука и обложки. Старый auto преобразуется в TikTok Sans Black. */
  font: z.preprocess(value => typeof value === 'string' ? LEGACY_SUBTITLE_FONTS[value] ?? value : value,
    z.enum(SUBTITLE_FONTS)).default(DEFAULTS.font),
  /** Старое «Авто» оставляет музыку включённой, выбирая спокойное настроение. */
  music: z.preprocess(value => value === 'auto' ? 'calm' : value,
    z.enum(['off', 'auto', ...MOODS])).default(DEFAULTS.music),
  /** Громкость музыки под голосом: тихо, средне, громко. */
  musicVolume: z.enum(MUSIC_VOLUMES).default(DEFAULTS.musicVolume),
  /** Как приводить горизонтальную запись к 9:16. */
  framing: z.enum(['auto', 'center', 'blur']).default(DEFAULTS.framing),
  /** Выключение фона не отключает выравнивание громкости речи. */
  noiseReduction: z.enum(NOISE_REDUCTION_MODES).default(DEFAULTS.noiseReduction),
  transition: z.enum(TRANSITIONS).default(DEFAULTS.transition),
  transitionDuration: z.number().min(TRANSITION_MIN_S).max(TRANSITION_MAX_S).default(DEFAULTS.transitionDuration),
  colorFilter: z.enum(COLOR_FILTERS).default(DEFAULTS.colorFilter),
  colorIntensity: z.number().min(0).max(100).default(DEFAULTS.colorIntensity),
  /** Язык описания и хэштегов. auto = язык речи. */
  captionLanguage: z.enum(['auto', 'ru', 'en']).default(DEFAULTS.captionLanguage),
});

export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;

// Zod 4 применяет вложенные default даже внутри .partial(). Для PATCH это
// сбрасывало бы режим и оформление при изменении лишь одной настройки.
type PatchShape = { [K in keyof typeof ProjectSettingsSchema.shape]: z.ZodOptional<ReturnType<(typeof ProjectSettingsSchema.shape)[K]['removeDefault']>> };
export const ProjectSettingsPatchSchema = z.object(Object.fromEntries(
  Object.entries(ProjectSettingsSchema.shape).map(([key, schema]) => [key, schema.removeDefault().optional()]),
) as PatchShape);

/** Литерал из constants.ts, типизированный схемой. */
export const DEFAULT_SETTINGS: ProjectSettings = DEFAULTS;

/** Лимит только для выжимки; полный рассказ никогда не обрезается по секундам. */
export function summaryDurationLimitS(settings: ProjectSettings): number | null {
  return settings.editingMode === 'summary' ? (settings.duration === 'auto' ? 60 : Number(settings.duration)) : null;
}
